<template>
  <component
    :disabled="pageLoading"
    :is="componentType"
    ref="input"
    :value="valueAsObject"
    :search-input.sync="term"
    :items="items"
    @focus="onFocus"
    @blur="onBlur"
    @input="
      emitValue($event, null);
      itemSelected = true;
    "
    item-value="id"
    item-text="name"
    no-filter
    hide-no-data
    :loading="loading"
    :prepend-inner-icon="allowGeolocation ? undefined : 'place'"
    :label="label"
    :placeholder="focused ? placeholder : ''"
    :menu-props="mergedMenuProps"
    :hint="hint"
    :persistent-hint="persistentHint"
    :aria-label="ariaLabel"
    filled
    :hide-details="hint ? 'auto' : true"
    clearable
  >
    <template v-slot:item="{ item }">
      <v-icon>{{ item.geo ? "star" : "place" }}</v-icon>
      {{ item.name }}
    </template>
    <!-- F-031: das Fadenkreuz wird als echter Button gerendert,
         damit es konsistent fokussierbar ist und einen aria-label traegt. -->
    <template v-slot:prepend-inner v-if="allowGeolocation">
      <v-btn
        icon
        x-small
        class="cr-geocoder-geolocate"
        aria-label="Aktuellen Standort uebernehmen"
        @click.stop="geolocate"
        @keydown.enter.prevent="geolocate"
        @keydown.space.prevent="geolocate"
      >
        <v-icon aria-hidden="true">my_location</v-icon>
      </v-btn>
    </template>
  </component>
</template>

<script>
import Vue from "vue";

import { VCombobox } from "vuetify/lib/components/VCombobox";
import { VSelect } from "vuetify/lib/components/VSelect";
import { decode } from "ngeohash";
import { fetchGeocoding } from "../api";

export default Vue.extend({
  name: "GeocoderSearch",

  props: {
    pageLoading: { type: Boolean, default: false },
    inputKey: { type: String, default: "key1" },
    value: { type: String, default: undefined },
    pois: { type: Array, default: () => [] },
    focusPoint: { type: Array, default: undefined },
    limitFocusDist: { type: Number, default: 0 },
    allowGeolocation: { type: Boolean, default: false },
    allowGeocoding: { type: Boolean, default: true },
    label: { type: String, default: "Adresse" },
    placeholder: { type: String, default: "Adresse oder Örtlichkeit" },
    menuProps: { type: Object, default: () => ({}) },
    hint: { type: String, default: "" },
    persistentHint: { type: Boolean, default: false },
    geocodeInputChanged: { type: Function },
  },

  data: () => ({
    // Timer for debouncing geocoding-request
    prevDeboucingTimer: 0,
    itemSelected: true,
    term: "",
    results: [],

    debounce: null,
    lastGeocodeCallId: 0,
    focused: false,
    changeSinceFocus: false,
    loading: false,
  }),
  watch: {
    /**
     * if term is changed then run geocoding
     * @param oldVal not used
     * @param newVal address value
     */
    term(oldVal, newVal) {
      if (newVal !== "" && newVal !== null) {
        this.geocode();
      }
    },
  },
  computed: {
    /**
     * POIs list (destination favorites)
     */
    items() {
      let p = this.pois.filter(({ name }) =>
        fuzzySearch(name, this.term || "")
      );
      const r = this.results;

      if (p.length && r.length) return [...p, { divider: true }, ...r];
      else return [...p, ...r];
    },

    /**
     * choosing selected POI value
     * interpret the string prop as ID and try to find a matching POI.
    // the result is used as input prop to the combobox.
     */
    valueAsObject() {
      const v = this.value || null;
      const p = this.pois.filter((p) => p.id === v || p.name === v)[0];
      return p || (v ? { id: v, name: v } : null);
    },
    /**
     * selection of component type, e.g. Combobox or Vselect
     */
    componentType() {
      if (this.allowGeocoding || this.pois.length > 14) return VCombobox;
      return VSelect;
    },
    /**
     * F-025: vollstaendige Beschreibung fuer Screenreader, die das Label
     * um Funktion und Bedienhinweise ergaenzt.
     */
    ariaLabel() {
      const base = this.label || "Adresse";
      return `${base}, Adress-Eingabe mit Vorschlagsliste. Pfeiltasten zur Auswahl, Enter zum Übernehmen.`;
    },
    /**
     * F-010 + F-025: Listbox eager in den DOM rendern, damit aria-owns /
     * aria-controls auf ein existierendes Element zeigen und Screenreader
     * den Combobox-Status zuverlaessig ankuendigen.
     */
    mergedMenuProps() {
      return { eager: true, ...(this.menuProps || {}) };
    },
  },

  methods: {
    /**
     * listener on User input change.
     * @param event
     * @param valueAsObject
     */
    async emitValue(event, valueAsObject) {
      let value;
      // if valueAsObject is passed, then use that
      if (valueAsObject) {
        value = valueAsObject;
      }
      // else if an input event is passed then extract its value
      else if (event instanceof Event) {
        try {
          value = event.target.value;
        } catch {}
      } else if (!this.allowGeocoding && this.pois.length < 14) {
        /* if geocoding is not allowed and there are less then 14 destinations (so VSelect instead of VCombo is used)
      then retrieve the chose from the drop-down list and identify it by its ID. 
      Otherwise with VSelect if the passed value in 'event' is used, then we get just the id without the geo-code */
        value = this.pois.find((obj) => obj.id === event);
      }
      // if an input field is emptied than set also the destination/origin lonLat to empty object to delete it within App.vue
      else if (!event) {
        this.geocodeInputChanged({}, this.inputKey);
      } else {
        value = event;
      }
      let v = value?.id || value;
      this.$emit("input", v);
      // this.changeSinceFocus = false;
      // if update-Function is given from parent then call it to return the geocode-obj with lat-lon
      if (this.geocodeInputChanged && value) {
        // if the location is encoded as geohash then decode it to get the coordinates
        if (value.geo && !value.lonLat) {
          var lonLat = decode(value.geo);
          value.lonLat = [lonLat.longitude, lonLat.latitude];
        }
        this.geocodeInputChanged(value, this.inputKey);
      }
      // TODO: hide menu
    },
    /**
     * handles deboucing by clearing previous timers if it's still active and starts a new timer
     * and by that overrides the former one
     * @param func: function to call on timeout
     * @param delay: delay in millesec
     */
    useDebounce(func, delay) {
      const debouncedFunction = (...args) => {
        if (this.prevDeboucingTimer) {
          clearTimeout(this.prevDeboucingTimer);
        }
        this.prevDeboucingTimer = setTimeout(() => {
          func(...args);
        }, delay);
      };
      return debouncedFunction;
    },
    /**
     * calls the API endpoint for fetching the geocoding
     */
    async geocodeDebounced() {
      if (
        !this.allowGeocoding ||
        !this.term ||
        this.term.length < 3 ||
        this.itemSelected
      ) {
        this.results = [];
        // F-029: Lade-Indikator auch im Early-Return zuruecksetzen
        this.loading = false;
        if (this.itemSelected) {
          this.itemSelected = false;
        }

        return;
      }
      this.loading = true;
      // F-029: try/finally garantiert, dass loading auch bei Fehlern zurueckgesetzt wird
      try {
        const results = await fetchGeocoding(this.term);
        // if an earlier call returns after a later call, ignore the result
        this.results = results;
      } catch (e) {
        console.error("Geocoding fehlgeschlagen:", e);
      } finally {
        this.loading = false;
      }
    },
    /**
     * F-027 / F-028: bei Fokus die POI-Liste oeffnen, wenn voreingestellte
     * Ziele vorhanden sind und noch kein Suchterm getippt wurde.
     */
    async onFocus() {
      this.focused = true;
      this.itemSelected = false;
      if (this.pois.length > 0 && !this.term) {
        await this.$nextTick();
        if (
          this.$refs.input &&
          typeof this.$refs.input.activateMenu === "function"
        ) {
          this.$refs.input.activateMenu();
        }
      }
    },
    /**
     * F-029: beim Verlassen des Feldes Lade-Indikator zuruecksetzen.
     */
    onBlur() {
      this.focused = false;
      this.loading = false;
    },
    /**
     * triggers the call of the debounced gecode-fetching function
     */
    geocode() {
      this.useDebounce(this.geocodeDebounced, 1000)();
    },
    /**
     *
     */
    async geolocate() {
      this.loading = true;
      this.results = await this.geolocateAddresses();
      this.$refs.input.focus();
      this.loading = false;
    },
    /**
     * request current location of user (not used currently)
     */
    async geolocateAddresses() {
      return Error("API endpoint on CR-Server not implemented yet");
      const geolocate = () =>
        new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej)
        );
      // const {
      //   coords: {longitude, latitude, accuracy},
      // } = await geolocate();
      // const geocoder = await selectService();
      // return geocoder.reverse([longitude, latitude]);
    },
  },
});

/**
 * TODO: use proper fuzzy search filter..
 * @param hay
 * @param needle
 */
function fuzzySearch(hay, needle) {
  if (!hay || !needle) return true;
  (hay = hay.toLowerCase()), (needle = needle.toLowerCase());
  let n = -1,
    l;
  for (let i = 0; (l = needle[i++]); )
    if (!~(n = hay.indexOf(l, n + 1))) return false;
  return true;
}
</script>
